import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { AnimatedWords } from "@/components/motion";

const IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1554284126-aa88f22d8b74?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "A diverse group of athletes lifting barbells together",
    span: "col-span-2 row-span-2",
  },
  {
    url: "https://images.unsplash.com/photo-1567013127542-490d757e51fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Black athlete training with battle ropes",
    span: "col-span-2 row-span-1",
  },
  {
    url: "https://images.unsplash.com/photo-1585152968992-851c3a8e1678?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Black athlete training with a dumbbell",
    span: "col-span-1 row-span-1",
  },
  {
    url: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Athlete pressing a barbell overhead",
    span: "col-span-1 row-span-1",
  },
];

export default function Gallery() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yUp = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  return (
    <section ref={ref} id="gallery" data-testid="gallery-section" className="relative bg-[#0A0A0A] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-teal" />
              <span className="font-condensed text-base tracking-[0.35em] text-teal">THE FLOOR</span>
            </div>
            <h2 className="font-display text-5xl leading-[0.9] uppercase sm:text-6xl md:text-7xl">
              <AnimatedWords text="Where energy" />
              <br />
              <span className="accent-teal">
                <AnimatedWords text="meets effort" delay={0.2} />
              </span>
            </h2>
          </div>
          <p className="max-w-sm text-base text-zinc-400">
            Real people, real intensity. Every rep is better with someone matched to your pace.
          </p>
        </div>

        <div className="grid auto-rows-[180px] grid-cols-2 gap-4 md:grid-cols-4 md:auto-rows-[220px]">
          {IMAGES.map((img, i) => (
            <motion.div
              key={i}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.1 }}
              style={i === 1 || i === 2 ? { y: yUp } : undefined}
              className={`group relative overflow-hidden border border-white/10 ${img.span}`}
            >
              <img
                src={img.url}
                alt={img.alt}
                className="h-full w-full object-cover grayscale transition-all duration-700 ease-out group-hover:scale-110 group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-black/30 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
